import Arrow from "@elsdoerfer/react-arrow";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import DoneOutlineIcon from "@mui/icons-material/DoneOutline";
import ErrorIcon from "@mui/icons-material/Error";
import RemoveIcon from "@mui/icons-material/Remove";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  Tooltip,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import SelectEntity from "components/SelectEntity";
import useGlobalContext from "hooks/globalData";
import { Field } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import {
  ImportJobTypeField,
  ListRequestOfBaseFilter,
  ListResponseOfImportJobTypeField,
} from "services/KnowfullyAdminClient";
import { Color } from "types/colors";

const styles = {
  row: {
    mt: 1,
    mb: 1,
    pb: 1,
    borderLeft: 3,
    borderLeftStyle: "solid",
    borderBottom: 1,
    borderBottomStyle: "solid",
    borderBottomColor: "divider",
  },
};

type RowState = "invalid" | "mapped" | "unmapped" | "pending";

const rowAccentColors: Record<RowState, string> = {
  invalid: "error.main",
  mapped: "success.main",
  unmapped: "warning.main",
  pending: "divider",
};

const rowState = ({
  highlighted,
  validated,
  validating,
  empty,
}: Pick<
  ImportsRowProps,
  "highlighted" | "validated" | "validating" | "empty"
>): RowState => {
  if (highlighted) {
    return "invalid";
  }

  if (validated && !validating) {
    return "mapped";
  }

  return empty ? "unmapped" : "pending";
};

type ImportsRowProps = {
  name: string;
  index: number;
  highlighted: boolean;
  validating: boolean;
  validated: boolean;
  required: boolean;
  evaluated: boolean;
  empty: boolean;
  excluded: { index: number; value: string }[];
  loaded: boolean;
  validationMessage: string;
  entryCheck: (value: string, index: number) => void;
  restartField: (index: number) => void;
};

export const ImportsRow = ({
  name,
  index,
  highlighted,
  validating,
  validated,
  required,
  evaluated,
  excluded,
  loaded,
  empty,
  validationMessage,
  entryCheck,
  restartField,
}: ImportsRowProps) => {
  const { migrationJobService } = useGlobalContext();

  const { formatMessage: t } = useIntl();

  const { id: migrationJobTypeId } = useParams<{ id: string }>();

  const fetchedMigrationJobTypeFields = useQuery<ListResponseOfImportJobTypeField>(
    ["migrationJobTypeFields", migrationJobTypeId],
    () =>
      migrationJobService.getImportJobTypeFields(
        migrationJobTypeId!,
        ListRequestOfBaseFilter.fromJS({
          sortField: "dataType",
          pageSize: -1,
        })
      )
  );

  const migrationJobTypeFields = fetchedMigrationJobTypeFields.data
    ?.data as ImportJobTypeField[];

  const state = rowState({ highlighted, validated, validating, empty });

  return (
    <Grid
      container
      spacing={3}
      sx={{ ...styles.row, borderLeftColor: rowAccentColors[state] }}
    >
      <Grid id="title" item xs={4}>
        <Field name={`${name}`}>
          {({ input }) => (
            <div style={{ paddingTop: "15px" }}>
              <Typography variant="h6" gutterBottom>
                {`${input.value}`}
              </Typography>
            </div>
          )}
        </Field>
      </Grid>
      <Grid item xs={3}>
        <Tooltip title={t({ id: "relatesToLabel" })}>
          <span>
            <Arrow
              angle={90}
              length={200}
              lineDashed={true}
              style={{
                width: "100%",
                marginTop: "20px",
              }}
            />
          </span>
        </Tooltip>
      </Grid>
      <Grid id="availableFields" item xs={5} style={{ display: "flex" }}>
        <Field name={`migrationTypeId_${index}`} required={required}>
          {({ input, meta }) => (
            <Grid item xs={12}>
              <FormControl fullWidth error={meta.error && meta.submitFailed}>
                <SelectEntity
                  fetchedData={fetchedMigrationJobTypeFields}
                  groupBy={(option: any) => option.dataTypeDescription}
                  disabled={
                    validating ||
                    (!input.value &&
                      excluded.length === migrationJobTypeFields.length) ||
                    !loaded
                  }
                  labelField="fieldName"
                  label={t({ id: "selectLabel" })}
                  {...input}
                  getOptionDisabled={(option) =>
                    excluded.some((x) => x.value === option.fieldName)
                  }
                  textFieldProps={{
                    error: meta.error && meta.submitFailed,
                    required: required,
                  }}
                  renderOption={(_: any, option: ImportJobTypeField) => {
                    const requiredConditionally = !!option
                      .requiredIfFieldsMissing?.length;

                    const arrowColor = option.required
                      ? Color.Red
                      : requiredConditionally
                      ? "#ff9800"
                      : "";

                    const backgroundColor = option.required
                      ? Color.LightRedLighter
                      : requiredConditionally
                      ? "#FFF4E5"
                      : "";

                    return (
                      <Box display="flex" width="100%">
                        <ArrowRightIcon style={{ color: arrowColor }} />
                        <Typography style={{ backgroundColor }}>
                          {option.fieldName}
                        </Typography>
                        <i
                          style={{
                            color: Color.LightGrey,
                            paddingLeft: "15px",
                          }}
                        >
                          {option.required ? (
                            <T id="requiredLabel" />
                          ) : requiredConditionally ? (
                            <T id="requiredConditionally" />
                          ) : null}
                        </i>
                      </Box>
                    );
                  }}
                  parentChange={async (value) => {
                    if (!value) {
                      await restartField(index);
                      return;
                    }
                    await entryCheck(value, index);
                  }}
                />
                {required && (
                  <Typography
                    style={{
                      fontSize: "10px",
                      color: "grey",
                    }}
                  >
                    {t({ id: "requiredLabel" })}
                  </Typography>
                )}
                {meta.error && meta.submitFailed && (
                  <FormHelperText>{meta.error}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          )}
        </Field>
        <Tooltip title={validationMessage}>
          <Box m={2} style={{ paddingTop: "3px" }}>
            {(validating || !loaded) && <CircularProgress size={24} />}
            {!validating && validated && evaluated && (
              <DoneOutlineIcon style={{ color: Color.Green }} />
            )}
            {!validating && !validated && !evaluated && loaded && (
              <RemoveIcon style={{ color: Color.Grey }} />
            )}

            {!validating && evaluated && !validated && empty && (
              <WarningIcon style={{ color: Color.Orange }} />
            )}
            {!validating && !validated && evaluated && !empty && (
              <ErrorIcon style={{ color: Color.Red }} />
            )}
          </Box>
        </Tooltip>
      </Grid>
    </Grid>
  );
};
